import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import toast from 'react-hot-toast';
import { 
  Settings, 
  Mail, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  Eye, 
  RefreshCw,
  AlertTriangle,
  Users,
  FileText,
  CreditCard,
  Wrench,
  Shield,
  Clock,
  Send,
  FlaskConical,
  X
} from 'lucide-react';

// Sample payloads for each event type used in test triggers
const TEST_PAYLOADS = {
  JOB_CARD_CREATED:        { customer_name: 'Test Customer', job_card_number: 'JC-TEST-001', vehicle_info: 'Toyota Camry 2022', date: new Date().toLocaleDateString() },
  JOB_CARD_IN_PROGRESS:    { customer_name: 'Test Customer', job_card_number: 'JC-TEST-001', vehicle_info: 'Toyota Camry 2022', date: new Date().toLocaleDateString() },
  JOB_CARD_ON_HOLD:        { customer_name: 'Test Customer', job_card_number: 'JC-TEST-001', vehicle_info: 'Toyota Camry 2022', date: new Date().toLocaleDateString() },
  JOB_CARD_COMPLETED:      { customer_name: 'Test Customer', job_card_number: 'JC-TEST-001', vehicle_info: 'Toyota Camry 2022', date: new Date().toLocaleDateString() },
  JOB_CARD_DELIVERED:      { customer_name: 'Test Customer', job_card_number: 'JC-TEST-001', vehicle_info: 'Toyota Camry 2022', date: new Date().toLocaleDateString() },
  JOB_CARD_CANCELLED:      { customer_name: 'Test Customer', job_card_number: 'JC-TEST-001', vehicle_info: 'Toyota Camry 2022', date: new Date().toLocaleDateString() },
  QUOTATION_CREATED:       { customer_name: 'Test Customer', quotation_number: 'QT-TEST-001', amount: '1,500.00', expiry_date: '2025-12-31', date: new Date().toLocaleDateString() },
  QUOTATION_SENT:          { customer_name: 'Test Customer', quotation_number: 'QT-TEST-001', amount: '1,500.00', date: new Date().toLocaleDateString() },
  QUOTATION_ACCEPTED:      { customer_name: 'Test Customer', quotation_number: 'QT-TEST-001', amount: '1,500.00', date: new Date().toLocaleDateString() },
  QUOTATION_REJECTED:      { customer_name: 'Test Customer', quotation_number: 'QT-TEST-001', amount: '1,500.00', date: new Date().toLocaleDateString() },
  QUOTATION_EXPIRED:       { customer_name: 'Test Customer', quotation_number: 'QT-TEST-001', amount: '1,500.00', date: new Date().toLocaleDateString() },
  INVOICE_CREATED:         { customer_name: 'Test Customer', invoice_number: 'INV-TEST-001', amount: '2,000.00', due_date: '2025-12-31', date: new Date().toLocaleDateString() },
  INVOICE_SENT:            { customer_name: 'Test Customer', invoice_number: 'INV-TEST-001', amount: '2,000.00', date: new Date().toLocaleDateString() },
  INVOICE_OVERDUE:         { customer_name: 'Test Customer', invoice_number: 'INV-TEST-001', amount: '2,000.00', date: new Date().toLocaleDateString() },
  PAYMENT_RECEIVED:        { customer_name: 'Test Customer', amount: '2,000.00', payment_method: 'Cash', reference: 'PAY-TEST-001', date: new Date().toLocaleDateString() },
  PARTIAL_PAYMENT_RECEIVED:{ customer_name: 'Test Customer', amount: '1,000.00', total_amount: '2,000.00', balance: '1,000.00', payment_method: 'Cash', reference: 'PAY-TEST-001', date: new Date().toLocaleDateString() },
  PAYMENT_FAILED:          { customer_name: 'Test Customer', amount: '2,000.00', reference: 'PAY-TEST-001', date: new Date().toLocaleDateString() },
  PAYMENT_REMINDER:        { customer_name: 'Test Customer', amount: '2,000.00', due_date: '2025-12-31', date: new Date().toLocaleDateString() },
  USER_APPROVED:           { name: 'Test User', username: 'testuser', date: new Date().toLocaleDateString() },
  USER_REJECTED:           { name: 'Test User', username: 'testuser', date: new Date().toLocaleDateString() },
  USER_SUSPENDED:          { name: 'Test User', username: 'testuser', date: new Date().toLocaleDateString() },
  LEAD_CREATED:            { lead_name: 'Test Lead', source: 'Website', date: new Date().toLocaleDateString() },
  LEAD_ASSIGNED:           { lead_name: 'Test Lead', assigned_to: 'Sales Rep', date: new Date().toLocaleDateString() },
  LEAD_CONVERTED:          { lead_name: 'Test Lead', customer_name: 'Test Customer', date: new Date().toLocaleDateString() },
  DEAL_WON:                { deal_name: 'Test Deal', amount: '5,000.00', date: new Date().toLocaleDateString() },
  DEAL_LOST:               { deal_name: 'Test Deal', amount: '5,000.00', date: new Date().toLocaleDateString() },
  FOLLOW_UP_DUE:           { lead_name: 'Test Lead', assigned_to: 'Sales Rep', date: new Date().toLocaleDateString() },
  LOW_STOCK_ALERT:         { part_name: 'Test Part', current_stock: 2, minimum_stock: 10, date: new Date().toLocaleDateString() },
  OUT_OF_STOCK:            { part_name: 'Test Part', date: new Date().toLocaleDateString() },
  STOCK_RECEIVED:          { part_name: 'Test Part', quantity: 50, date: new Date().toLocaleDateString() },
  PURCHASE_ORDER_CREATED:  { po_number: 'PO-TEST-001', vendor_name: 'Test Vendor', amount: '3,000.00', date: new Date().toLocaleDateString() },
  PURCHASE_ORDER_APPROVED: { po_number: 'PO-TEST-001', vendor_name: 'Test Vendor', amount: '3,000.00', date: new Date().toLocaleDateString() },
  VENDOR_PAYMENT_DUE:      { vendor_name: 'Test Vendor', amount: '3,000.00', due_date: '2025-12-31', date: new Date().toLocaleDateString() },
  SERVICE_REMINDER_DUE:    { customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022', service_type: 'Oil Change', date: new Date().toLocaleDateString() },
  SERVICE_REMINDER_OVERDUE:{ customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022', service_type: 'Oil Change', date: new Date().toLocaleDateString() },
  INSURANCE_EXPIRY_REMINDER:{ customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022', expiry_date: '2025-12-31', date: new Date().toLocaleDateString() },
  REGISTRATION_EXPIRY_REMINDER:{ customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022', expiry_date: '2025-12-31', date: new Date().toLocaleDateString() },
  DAILY_SALES_REPORT:      { date: new Date().toLocaleDateString(), total_sales: '15,000.00', total_jobs: 12 },
  WEEKLY_PROFIT_LOSS:      { week: 'Week 30', profit: '8,000.00', loss: '2,000.00', date: new Date().toLocaleDateString() },
  MONTHLY_FINANCIAL_SUMMARY:{ month: 'July 2025', revenue: '60,000.00', expenses: '20,000.00', date: new Date().toLocaleDateString() },
  DAY_END_RECONCILIATION:  { date: new Date().toLocaleDateString(), total: '15,000.00', cash: '5,000.00', card: '10,000.00' },
  OUTSTANDING_RECEIVABLES: { date: new Date().toLocaleDateString(), total_outstanding: '25,000.00', count: 8 },
  CASH_FLOW_ALERT:         { date: new Date().toLocaleDateString(), current_balance: '5,000.00', threshold: '10,000.00' },
  JOB_ASSIGNED:            { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022', services: 'Oil Change, Brake Check', estimated_time: '2' },
  JOB_REASSIGNED:          { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022' },
  JOB_PRIORITY_CHANGED:    { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', priority: 'High', date: new Date().toLocaleDateString() },
  PARTS_AVAILABLE:         { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', part_name: 'Test Part', date: new Date().toLocaleDateString() },
  PARTS_REQUESTED:         { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', part_name: 'Test Part', quantity: 2, date: new Date().toLocaleDateString() },
  SHIFT_REMINDER:          { technician_name: 'Test Technician', shift_time: '08:00 AM', date: new Date().toLocaleDateString() },
  OVERTIME_APPROVED:       { technician_name: 'Test Technician', hours: 2, date: new Date().toLocaleDateString() },
  PERFORMANCE_REVIEW:      { technician_name: 'Test Technician', period: 'July 2025', date: new Date().toLocaleDateString() },
  DAILY_BACKUP_REMINDER:   { date: new Date().toLocaleDateString() },
  BACKUP_COMPLETED:        { date: new Date().toLocaleDateString(), size: '250MB' },
  BACKUP_FAILED:           { date: new Date().toLocaleDateString(), error: 'Disk space insufficient' },
  SYSTEM_MAINTENANCE:      { date: new Date().toLocaleDateString(), scheduled_time: '02:00 AM', duration: '2 hours' },
  DATABASE_CLEANUP:        { date: new Date().toLocaleDateString(), records_removed: 1500 },
  JOB_IN_PROGRESS:         { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022' },
  JOB_ON_HOLD:             { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022', reason: 'Waiting for parts' },
  JOB_COMPLETED_BY_TECH:   { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022', date: new Date().toLocaleDateString() },
  JOB_CANCELLED:           { technician_name: 'Test Technician', job_card_number: 'JC-TEST-001', customer_name: 'Test Customer', vehicle_info: 'Toyota Camry 2022' },
};

const NotificationControlPanel = ({ onNavigate }) => {
  const [rules, setRules] = useState([]);
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({});
  const [testModal, setTestModal] = useState(null); // { eventType, rule }
  const [testRecipient, setTestRecipient] = useState('');
  const [testChannel, setTestChannel] = useState('EMAIL');
  const [testing, setTesting] = useState(false);

  const eventTypeConfig = {
    // Job Card Workflow
    'JOB_CARD_CREATED': {
      icon: Wrench,
      color: 'text-blue-600 bg-blue-100',
      category: 'Customer',
      description: 'When a new job card is created',
      recipients: 'Customer'
    },
    'JOB_CARD_IN_PROGRESS': {
      icon: Clock,
      color: 'text-yellow-600 bg-yellow-100',
      category: 'Customer',
      description: 'When job card work begins',
      recipients: 'Customer'
    },
    'JOB_CARD_ON_HOLD': {
      icon: AlertTriangle,
      color: 'text-orange-600 bg-orange-100',
      category: 'Customer',
      description: 'When job card is put on hold',
      recipients: 'Customer'
    },
    'JOB_CARD_COMPLETED': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Customer',
      description: 'When a job card is marked as completed',
      recipients: 'Customer'
    },
    'JOB_CARD_DELIVERED': {
      icon: CheckCircle,
      color: 'text-emerald-600 bg-emerald-100',
      category: 'Customer',
      description: 'When vehicle is delivered to customer',
      recipients: 'Customer'
    },
    'JOB_CARD_CANCELLED': {
      icon: XCircle,
      color: 'text-red-600 bg-red-100',
      category: 'Customer',
      description: 'When job card is cancelled',
      recipients: 'Customer'
    },

    // Quotation Workflow
    'QUOTATION_CREATED': {
      icon: FileText,
      color: 'text-blue-600 bg-blue-100',
      category: 'Customer',
      description: 'When a new quotation is created',
      recipients: 'Customer'
    },
    'QUOTATION_SENT': {
      icon: Send,
      color: 'text-indigo-600 bg-indigo-100',
      category: 'Customer',
      description: 'When quotation is sent to customer',
      recipients: 'Customer'
    },
    'QUOTATION_ACCEPTED': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Customer',
      description: 'When a quotation is accepted by customer',
      recipients: 'Customer'
    },
    'QUOTATION_REJECTED': {
      icon: XCircle,
      color: 'text-red-600 bg-red-100',
      category: 'Customer',
      description: 'When quotation is rejected',
      recipients: 'Customer'
    },
    'QUOTATION_EXPIRED': {
      icon: Clock,
      color: 'text-orange-600 bg-orange-100',
      category: 'Customer',
      description: 'When quotation expires',
      recipients: 'Customer'
    },

    // Invoice & Payment Workflow
    'INVOICE_CREATED': {
      icon: FileText,
      color: 'text-blue-600 bg-blue-100',
      category: 'Customer',
      description: 'When a new invoice is generated',
      recipients: 'Customer'
    },
    'INVOICE_SENT': {
      icon: Send,
      color: 'text-indigo-600 bg-indigo-100',
      category: 'Customer',
      description: 'When invoice is sent to customer',
      recipients: 'Customer'
    },
    'INVOICE_OVERDUE': {
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      category: 'Customer',
      description: 'When invoice becomes overdue',
      recipients: 'Customer'
    },
    'PAYMENT_RECEIVED': {
      icon: CreditCard,
      color: 'text-green-600 bg-green-100',
      category: 'Customer',
      description: 'When a payment is successfully processed',
      recipients: 'Customer'
    },
    'PARTIAL_PAYMENT_RECEIVED': {
      icon: CreditCard,
      color: 'text-yellow-600 bg-yellow-100',
      category: 'Customer',
      description: 'When partial payment is received',
      recipients: 'Customer'
    },
    'PAYMENT_FAILED': {
      icon: XCircle,
      color: 'text-red-600 bg-red-100',
      category: 'Customer',
      description: 'When payment processing fails',
      recipients: 'Customer'
    },
    'PAYMENT_REMINDER': {
      icon: Clock,
      color: 'text-orange-600 bg-orange-100',
      category: 'Customer',
      description: 'Payment reminder notifications',
      recipients: 'Customer'
    },

    // User & Admin Notifications
    'USER_APPROVED': {
      icon: Users,
      color: 'text-green-600 bg-green-100',
      category: 'Admin',
      description: 'When a user account is approved',
      recipients: 'User'
    },
    'USER_REJECTED': {
      icon: XCircle,
      color: 'text-red-600 bg-red-100',
      category: 'Admin',
      description: 'When a user account is rejected',
      recipients: 'User'
    },
    'USER_SUSPENDED': {
      icon: Shield,
      color: 'text-orange-600 bg-orange-100',
      category: 'Admin',
      description: 'When a user account is suspended',
      recipients: 'User'
    },

    // CRM Notifications
    'LEAD_CREATED': {
      icon: Users,
      color: 'text-blue-600 bg-blue-100',
      category: 'Admin',
      description: 'When a new lead is created',
      recipients: 'Sales Team'
    },
    'LEAD_ASSIGNED': {
      icon: Users,
      color: 'text-indigo-600 bg-indigo-100',
      category: 'Admin',
      description: 'When lead is assigned to team member',
      recipients: 'Assigned User'
    },
    'LEAD_CONVERTED': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Admin',
      description: 'When lead is converted to customer',
      recipients: 'Sales Team'
    },
    'DEAL_WON': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Admin',
      description: 'When a deal is marked as won',
      recipients: 'Sales Team'
    },
    'DEAL_LOST': {
      icon: XCircle,
      color: 'text-red-600 bg-red-100',
      category: 'Admin',
      description: 'When a deal is marked as lost',
      recipients: 'Sales Team'
    },
    'FOLLOW_UP_DUE': {
      icon: Clock,
      color: 'text-orange-600 bg-orange-100',
      category: 'Admin',
      description: 'When follow-up is due for lead/customer',
      recipients: 'Assigned User'
    },

    // Inventory Notifications
    'LOW_STOCK_ALERT': {
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      category: 'Admin',
      description: 'When inventory falls below minimum level',
      recipients: 'Inventory Manager'
    },
    'OUT_OF_STOCK': {
      icon: XCircle,
      color: 'text-red-600 bg-red-100',
      category: 'Admin',
      description: 'When item is out of stock',
      recipients: 'Inventory Manager'
    },
    'STOCK_RECEIVED': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Admin',
      description: 'When new stock is received',
      recipients: 'Inventory Manager'
    },
    'PURCHASE_ORDER_CREATED': {
      icon: FileText,
      color: 'text-blue-600 bg-blue-100',
      category: 'Admin',
      description: 'When purchase order is created',
      recipients: 'Procurement Team'
    },
    'PURCHASE_ORDER_APPROVED': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Admin',
      description: 'When purchase order is approved',
      recipients: 'Vendor'
    },
    'VENDOR_PAYMENT_DUE': {
      icon: CreditCard,
      color: 'text-orange-600 bg-orange-100',
      category: 'Admin',
      description: 'When vendor payment is due',
      recipients: 'Accounts Team'
    },

    // Service Reminders
    'SERVICE_REMINDER_DUE': {
      icon: Clock,
      color: 'text-orange-600 bg-orange-100',
      category: 'Customer',
      description: 'When vehicle service is due',
      recipients: 'Customer'
    },
    'SERVICE_REMINDER_OVERDUE': {
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      category: 'Customer',
      description: 'When service reminder is overdue',
      recipients: 'Customer'
    },
    'INSURANCE_EXPIRY_REMINDER': {
      icon: Shield,
      color: 'text-orange-600 bg-orange-100',
      category: 'Customer',
      description: 'When vehicle insurance is expiring',
      recipients: 'Customer'
    },
    'REGISTRATION_EXPIRY_REMINDER': {
      icon: FileText,
      color: 'text-orange-600 bg-orange-100',
      category: 'Customer',
      description: 'When vehicle registration is expiring',
      recipients: 'Customer'
    },

    // Accounting & Reports
    'DAILY_SALES_REPORT': {
      icon: FileText,
      color: 'text-blue-600 bg-blue-100',
      category: 'Reports',
      description: 'Daily sales summary report',
      recipients: 'Management'
    },
    'WEEKLY_PROFIT_LOSS': {
      icon: FileText,
      color: 'text-green-600 bg-green-100',
      category: 'Reports',
      description: 'Weekly profit and loss report',
      recipients: 'Management'
    },
    'MONTHLY_FINANCIAL_SUMMARY': {
      icon: FileText,
      color: 'text-purple-600 bg-purple-100',
      category: 'Reports',
      description: 'Monthly financial summary',
      recipients: 'Management'
    },
    'DAY_END_RECONCILIATION': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Reports',
      description: 'Daily accounts reconciliation',
      recipients: 'Accounts Team'
    },
    'OUTSTANDING_RECEIVABLES': {
      icon: CreditCard,
      color: 'text-orange-600 bg-orange-100',
      category: 'Reports',
      description: 'Outstanding receivables report',
      recipients: 'Accounts Team'
    },
    'CASH_FLOW_ALERT': {
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      category: 'Reports',
      description: 'Cash flow alerts and warnings',
      recipients: 'Management'
    },

    // Technician Notifications
    'JOB_ASSIGNED': {
      icon: Wrench,
      color: 'text-blue-600 bg-blue-100',
      category: 'Technician',
      description: 'When a job is assigned to technician',
      recipients: 'Technician'
    },
    'JOB_REASSIGNED': {
      icon: RefreshCw,
      color: 'text-orange-600 bg-orange-100',
      category: 'Technician',
      description: 'When a job is reassigned to another technician',
      recipients: 'Technician'
    },
    'JOB_PRIORITY_CHANGED': {
      icon: AlertTriangle,
      color: 'text-red-600 bg-red-100',
      category: 'Technician',
      description: 'When job priority is changed',
      recipients: 'Technician'
    },
    'PARTS_AVAILABLE': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Technician',
      description: 'When requested parts become available',
      recipients: 'Technician'
    },
    'PARTS_REQUESTED': {
      icon: AlertTriangle,
      color: 'text-yellow-600 bg-yellow-100',
      category: 'Technician',
      description: 'When technician requests parts',
      recipients: 'Inventory Manager'
    },
    'SHIFT_REMINDER': {
      icon: Clock,
      color: 'text-blue-600 bg-blue-100',
      category: 'Technician',
      description: 'Shift start/end reminders',
      recipients: 'Technician'
    },
    'OVERTIME_APPROVED': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'Technician',
      description: 'When overtime request is approved',
      recipients: 'Technician'
    },
    'PERFORMANCE_REVIEW': {
      icon: Users,
      color: 'text-purple-600 bg-purple-100',
      category: 'Technician',
      description: 'Performance review notifications',
      recipients: 'Technician'
    },

    // System Notifications
    'DAILY_BACKUP_REMINDER': {
      icon: Shield,
      color: 'text-blue-600 bg-blue-100',
      category: 'System',
      description: 'Daily system backup reminders',
      recipients: 'Admin'
    },
    'BACKUP_COMPLETED': {
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100',
      category: 'System',
      description: 'When system backup is completed',
      recipients: 'Admin'
    },
    'BACKUP_FAILED': {
      icon: XCircle,
      color: 'text-red-600 bg-red-100',
      category: 'System',
      description: 'When system backup fails',
      recipients: 'Admin'
    },
    'SYSTEM_MAINTENANCE': {
      icon: Settings,
      color: 'text-orange-600 bg-orange-100',
      category: 'System',
      description: 'System maintenance notifications',
      recipients: 'Admin'
    },
    'DATABASE_CLEANUP': {
      icon: RefreshCw,
      color: 'text-blue-600 bg-blue-100',
      category: 'System',
      description: 'Database cleanup notifications',
      recipients: 'Admin'
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [rulesRes, settingsRes, logsRes] = await Promise.all([
        axios.get('/api/notifications/rules'),
        axios.get('/api/notifications/settings'),
        axios.get('/api/notifications/logs?limit=100')
      ]);

      setRules(rulesRes.data || []);
      setSettings(settingsRes.data || []);
      
      // Calculate stats
      const logs = logsRes.data.logs || [];
      const last24h = logs.filter(log => 
        new Date(log.createdAt) > new Date(Date.now() - 24 * 60 * 60 * 1000)
      );
      
      setStats({
        total: logs.length,
        last24h: last24h.length,
        success: logs.filter(log => log.status === 'SUCCESS').length,
        failed: logs.filter(log => log.status === 'FAILED').length,
        byChannel: {
          EMAIL: logs.filter(log => log.channel === 'EMAIL').length,
          WHATSAPP: logs.filter(log => log.channel === 'WHATSAPP').length
        }
      });
    } catch (error) {
      toast.error('Failed to load notification data');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRule = async (ruleId, updates) => {
    try {
      setSaving(true);
      await axios.put(`/api/notifications/rules/${ruleId}`, updates);
      setRules(prev => prev.map(rule => 
        rule.id === ruleId ? { ...rule, ...updates } : rule
      ));
      toast.success('Notification rule updated');
    } catch (error) {
      toast.error('Failed to update rule');
    } finally {
      setSaving(false);
    }
  };

  const createMissingRules = async () => {
    try {
      setSaving(true);
      const existingEventTypes = rules.map(rule => rule.eventType);
      const missingEventTypes = Object.keys(eventTypeConfig).filter(
        eventType => !existingEventTypes.includes(eventType)
      );

      for (const eventType of missingEventTypes) {
        await axios.post('/api/notifications/rules', {
          eventType,
          channels: ['EMAIL', 'WHATSAPP'],
          enabled: true
        });
      }

      toast.success(`Created ${missingEventTypes.length} missing notification rules`);
      fetchData();
    } catch (error) {
      toast.error('Failed to create missing rules');
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (rule) => {
    await updateRule(rule.id, { enabled: !rule.enabled });
  };

  const toggleChannel = async (rule, channel) => {
    const newChannels = rule.channels.includes(channel)
      ? rule.channels.filter(c => c !== channel)
      : [...rule.channels, channel];
    
    await updateRule(rule.id, { channels: newChannels });
  };

  const getChannelStatus = (channel) => {
    const setting = settings.find(s => s.channel === channel);
    return setting?.enabled || false;
  };

  const getCategoryRules = (category) => {
    return rules.filter(rule => 
      eventTypeConfig[rule.eventType]?.category === category
    );
  };

  const openTestModal = (rule) => {
    setTestModal(rule);
    setTestRecipient('');
    setTestChannel(rule.channels.includes('EMAIL') ? 'EMAIL' : rule.channels[0] || 'EMAIL');
  };

  const closeTestModal = () => {
    setTestModal(null);
    setTestRecipient('');
  };

  const runTest = async () => {
    if (!testRecipient.trim()) {
      toast.error('Please enter a recipient');
      return;
    }
    try {
      setTesting(true);
      const payload = TEST_PAYLOADS[testModal.eventType] || {};
      // Inject recipient into the payload based on channel
      const data = {
        ...payload,
        ...(testChannel === 'EMAIL' ? { email: testRecipient } : { phone: testRecipient }),
        _testRecipient: testRecipient,
        _testChannel: testChannel,
      };
      await axios.post('/api/notifications/trigger', { eventType: testModal.eventType, data });
      toast.success(`Test notification triggered for ${testModal.eventType.replace(/_/g, ' ')}`);
      closeTestModal();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const categories = ['Customer', 'Admin', 'Technician', 'Reports', 'System'];

  if (loading) {
    return (
      <div className="max-w-6xl">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Notification Control Panel</h2>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">Loading notification settings...</span>
        </div>
      </div>
    );
  }

  const missingRules = Object.keys(eventTypeConfig).filter(
    eventType => !rules.some(rule => rule.eventType === eventType)
  );

  return (
    <div className="max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Notification Control Panel</h2>
          <p className="text-gray-500 mt-1">Centralized control for all system notifications</p>
        </div>
        <div className="flex gap-3">
          <button onClick={fetchData} disabled={loading}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {missingRules.length > 0 && (
            <button onClick={createMissingRules} disabled={saving}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50">
              <Settings className="w-4 h-4" />
              Create Missing Rules ({missingRules.length})
            </button>
          )}
        </div>
      </div>

      {/* Channel Status Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Email Channel</p>
              <div className="flex items-center gap-2 mt-1">
                <Mail className="w-4 h-4 text-blue-600" />
                <span className={`text-sm font-medium ${getChannelStatus('EMAIL') ? 'text-green-600' : 'text-red-600'}`}>
                  {getChannelStatus('EMAIL') ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
            {getChannelStatus('EMAIL') ? 
              <CheckCircle className="w-8 h-8 text-green-500" /> : 
              <XCircle className="w-8 h-8 text-red-500" />
            }
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{stats.byChannel?.EMAIL || 0}</p>
          <p className="text-xs text-gray-500">Total sent</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">WhatsApp Channel</p>
              <div className="flex items-center gap-2 mt-1">
                <MessageSquare className="w-4 h-4 text-green-600" />
                <span className={`text-sm font-medium ${getChannelStatus('WHATSAPP') ? 'text-green-600' : 'text-red-600'}`}>
                  {getChannelStatus('WHATSAPP') ? 'Active' : 'Disabled'}
                </span>
              </div>
            </div>
            {getChannelStatus('WHATSAPP') ? 
              <CheckCircle className="w-8 h-8 text-green-500" /> : 
              <XCircle className="w-8 h-8 text-red-500" />
            }
          </div>
          <p className="text-2xl font-bold text-gray-900 mt-2">{stats.byChannel?.WHATSAPP || 0}</p>
          <p className="text-xs text-gray-500">Total sent</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <div className="flex items-center gap-2 mt-1">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="text-sm font-medium text-green-600">
                  {stats.total > 0 ? Math.round((stats.success / stats.total) * 100) : 0}%
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-green-600">{stats.success || 0}</p>
              <p className="text-xs text-gray-500">of {stats.total || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Last 24 Hours</p>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-600">Recent Activity</span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-blue-600">{stats.last24h || 0}</p>
              <p className="text-xs text-gray-500">notifications</p>
            </div>
          </div>
        </div>
      </div>

      {/* Warning for disabled channels */}
      {(!getChannelStatus('EMAIL') && !getChannelStatus('WHATSAPP')) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <h3 className="font-medium text-red-900">All Notification Channels Disabled</h3>
          </div>
          <p className="text-sm text-red-700 mt-1">
            Both Email and WhatsApp channels are disabled. No notifications will be sent until at least one channel is enabled.
          </p>
        </div>
      )}

      {/* Notification Rules by Category */}
      {categories.map(category => {
        const categoryRules = getCategoryRules(category);
        if (categoryRules.length === 0) return null;

        return (
          <div key={category} className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{category} Notifications</h3>
              <span className="text-sm text-gray-500">({categoryRules.length} rules)</span>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Event Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Recipients
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        WhatsApp
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Test
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {categoryRules.map(rule => {
                      const config = eventTypeConfig[rule.eventType];
                      const IconComponent = config?.icon || Settings;
                      
                      return (
                        <tr key={rule.id} className={rule.enabled ? '' : 'opacity-60 bg-gray-50'}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg ${config?.color || 'text-gray-600 bg-gray-100'}`}>
                                <IconComponent className="w-4 h-4" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {rule.eventType.replace(/_/g, ' ')}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {config?.description || 'System notification'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-600">
                              {config?.recipients || 'System'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => toggleChannel(rule, 'EMAIL')}
                              disabled={saving || !getChannelStatus('EMAIL')}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                                rule.channels.includes('EMAIL') && getChannelStatus('EMAIL')
                                  ? 'bg-blue-600' 
                                  : 'bg-gray-200'
                              }`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                rule.channels.includes('EMAIL') && getChannelStatus('EMAIL') 
                                  ? 'translate-x-6' 
                                  : 'translate-x-1'
                              }`} />
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => toggleChannel(rule, 'WHATSAPP')}
                              disabled={saving || !getChannelStatus('WHATSAPP')}
                              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                                rule.channels.includes('WHATSAPP') && getChannelStatus('WHATSAPP')
                                  ? 'bg-green-600' 
                                  : 'bg-gray-200'
                              }`}
                            >
                              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                rule.channels.includes('WHATSAPP') && getChannelStatus('WHATSAPP') 
                                  ? 'translate-x-6' 
                                  : 'translate-x-1'
                              }`} />
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              rule.enabled 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {rule.enabled ? 'Enabled' : 'Disabled'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => toggleRule(rule)}
                              disabled={saving}
                              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                                rule.enabled
                                  ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                  : 'bg-green-100 text-green-700 hover:bg-green-200'
                              }`}
                            >
                              {rule.enabled ? 'Disable' : 'Enable'}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center">
                            <button
                              onClick={() => openTestModal(rule)}
                              disabled={saving}
                              title={`Test ${rule.eventType.replace(/_/g, ' ')}`}
                              className="px-3 py-1 rounded-md text-sm font-medium bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors disabled:opacity-50 flex items-center gap-1 mx-auto"
                            >
                              <FlaskConical className="w-3 h-3" />
                              Test
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}

      {/* Missing Rules Warning */}
      {missingRules.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-yellow-600" />
            <h3 className="font-medium text-yellow-900">Missing Notification Rules</h3>
          </div>
          <p className="text-sm text-yellow-700 mb-3">
            The following event types don't have notification rules configured:
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            {missingRules.map(eventType => (
              <span key={eventType} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                {eventType.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
          <button onClick={createMissingRules} disabled={saving}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 text-sm disabled:opacity-50">
            Create Missing Rules
          </button>
        </div>
      )}

      {/* Test Modal */}
      {testModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Test Notification</h3>
                <p className="text-sm text-gray-500">{testModal.eventType.replace(/_/g, ' ')}</p>
              </div>
              <button onClick={closeTestModal} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Channel</label>
                <div className="flex gap-3">
                  {['EMAIL', 'WHATSAPP'].map(ch => (
                    <button
                      key={ch}
                      onClick={() => setTestChannel(ch)}
                      disabled={!getChannelStatus(ch)}
                      className={`flex-1 py-2 px-3 rounded-md border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                        testChannel === ch
                          ? ch === 'EMAIL' ? 'bg-blue-600 text-white border-blue-600' : 'bg-green-600 text-white border-green-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {ch === 'EMAIL' ? <span className="flex items-center justify-center gap-1"><Mail className="w-3 h-3" />Email</span> : <span className="flex items-center justify-center gap-1"><MessageSquare className="w-3 h-3" />WhatsApp</span>}
                    </button>
                  ))}
                </div>
                {!getChannelStatus('EMAIL') && !getChannelStatus('WHATSAPP') && (
                  <p className="text-xs text-red-600 mt-1">No channels are enabled. Enable a channel first.</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {testChannel === 'EMAIL' ? 'Email Address' : 'Phone Number (with country code)'}
                </label>
                <input
                  type={testChannel === 'EMAIL' ? 'email' : 'tel'}
                  value={testRecipient}
                  onChange={e => setTestRecipient(e.target.value)}
                  placeholder={testChannel === 'EMAIL' ? 'test@example.com' : '+1234567890'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="bg-gray-50 rounded-md p-3">
                <p className="text-xs font-medium text-gray-600 mb-1">Sample payload that will be sent:</p>
                <pre className="text-xs text-gray-500 overflow-auto max-h-32">{JSON.stringify(TEST_PAYLOADS[testModal.eventType] || {}, null, 2)}</pre>
              </div>
            </div>
            <div className="flex gap-3 p-4 border-t">
              <button onClick={closeTestModal} className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={runTest}
                disabled={testing || !testRecipient.trim() || (!getChannelStatus('EMAIL') && !getChannelStatus('WHATSAPP'))}
                className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md text-sm font-medium hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {testing ? <><RefreshCw className="w-4 h-4 animate-spin" />Sending...</> : <><Send className="w-4 h-4" />Send Test</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-gray-50 rounded-lg p-6 mt-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => onNavigate && onNavigate('channels')}
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors text-left"
          >
            <Settings className="w-6 h-6 text-blue-600 mb-2" />
            <h4 className="font-medium text-gray-900">Channel Settings</h4>
            <p className="text-sm text-gray-500">Configure Email and WhatsApp channels</p>
          </button>
          
          <button
            onClick={() => onNavigate && onNavigate('templates')}
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-purple-300 transition-colors text-left"
          >
            <FileText className="w-6 h-6 text-purple-600 mb-2" />
            <h4 className="font-medium text-gray-900">Message Templates</h4>
            <p className="text-sm text-gray-500">Edit notification message templates</p>
          </button>
          
          <button
            onClick={() => { fetchData(); }}
            className="p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-colors text-left"
          >
            <Eye className="w-6 h-6 text-green-600 mb-2" />
            <h4 className="font-medium text-gray-900">View Logs</h4>
            <p className="text-sm text-gray-500">Monitor notification delivery status</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationControlPanel;